import React, { useState, useEffect, useCallback } from 'react';
import { UtensilsCrossed, Plus, Edit2, Trash2, CheckCircle, Circle, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const MEALS = ['breakfast', 'lunch', 'snacks', 'dinner'];
const MEAL_ICONS = { breakfast: '🍞', lunch: '🍱', snacks: '🍪', dinner: '🍛' };

const FoodMess = () => {
  const { token } = useAuth();
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [tenants, setTenants] = useState([]);
  const [grid, setGrid] = useState({});
  const [summary, setSummary] = useState(null);
  const [attendance, setAttendance] = useState({}); // {tenantId_meal: attended_bool}
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('menu');
  const [editing, setEditing] = useState(null); // {day, meal_type, item}
  const today = new Date().toISOString().slice(0, 10);
  const [attendanceDate, setAttendanceDate] = useState(today);

  const fetchProps = useCallback(async () => {
    const res = await axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } });
    setProperties(res.data);
    if (res.data.length && !propertyId) setPropertyId(res.data[0].id);
  }, [token, propertyId]);

  const fetchMenu = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/food/menu?property_id=${propertyId}`, { headers: { Authorization: `Bearer ${token}` } });
      setGrid(res.data.grid);
    } finally { setLoading(false); }
  }, [token, propertyId]);

  const fetchAttendance = useCallback(async () => {
    if (!propertyId) return;
    const [sRes, tRes, aRes] = await Promise.all([
      axios.get(`${API}/food/summary?property_id=${propertyId}&date=${attendanceDate}`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/tenants?property_id=${propertyId}`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/food/attendance?property_id=${propertyId}&date=${attendanceDate}`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    setSummary(sRes.data);
    setTenants(tRes.data.filter(t => t.status === 'active'));
    const m = {};
    aRes.data.forEach(a => { m[`${a.tenant_id}_${a.meal_type}`] = a.attended; });
    setAttendance(m);
  }, [token, propertyId, attendanceDate]);

  useEffect(() => { fetchProps(); }, [fetchProps]);
  useEffect(() => { if (tab === 'menu') fetchMenu(); else fetchAttendance(); }, [tab, fetchMenu, fetchAttendance]);

  const handleSaveMenu = async (day, meal_type, items, description) => {
    try {
      await axios.post(`${API}/food/menu`, {
        property_id: propertyId, day, meal_type,
        items: items.split(',').map(s => s.trim()).filter(Boolean),
        description,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Menu updated' });
      setEditing(null); fetchMenu();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (item) => {
    if (!item?.id) { setEditing(null); return; }
    try {
      await axios.delete(`${API}/food/menu/${item.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Menu item deleted' }); setEditing(null); fetchMenu();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const toggleAttendance = async (tenant, meal) => {
    const key = `${tenant.id}_${meal}`;
    const current = attendance[key] ?? false;
    try {
      await axios.post(`${API}/food/attendance`, {
        tenant_id: tenant.id, property_id: propertyId, date: attendanceDate, meal_type: meal, attended: !current
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAttendance(prev => ({ ...prev, [key]: !current }));
      if (summary) {
        const b = { ...summary.by_meal };
        if (!current) { b[meal].attended += 1; } else { b[meal].attended = Math.max(0, b[meal].attended - 1); }
        setSummary({ ...summary, by_meal: b });
      }
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="food-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2"><UtensilsCrossed /> Food / Mess</h1>
          <p className="text-gray-600 mt-1">Weekly menu & daily attendance</p>
        </div>
        <select
          data-testid="food-property-select"
          value={propertyId}
          onChange={e => setPropertyId(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg"
        >
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      <div className="flex gap-2 mb-6 border-b">
        <button data-testid="food-tab-menu" onClick={() => setTab('menu')} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'menu' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600'}`}>Weekly Menu</button>
        <button data-testid="food-tab-attendance" onClick={() => setTab('attendance')} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'attendance' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600'}`}>Attendance</button>
      </div>

      {tab === 'menu' && (
        <div data-testid="menu-content">
          {loading ? (
            <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-xl shadow-sm border border-gray-200">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Day</th>
                    {MEALS.map(m => (
                      <th key={m} className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">{MEAL_ICONS[m]} {m}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DAYS.map(day => (
                    <tr key={day} className="border-b hover:bg-gray-50" data-testid={`menu-row-${day}`}>
                      <td className="p-3 capitalize font-semibold text-gray-900">{day}</td>
                      {MEALS.map(meal => {
                        const item = grid[day]?.[meal];
                        return (
                          <td
                            key={meal}
                            onClick={() => setEditing({ day, meal_type: meal, item })}
                            data-testid={`menu-cell-${day}-${meal}`}
                            className="p-3 cursor-pointer hover:bg-blue-50 min-w-[140px]"
                          >
                            {item ? (
                              <div>
                                <p className="text-sm font-medium text-gray-800">{(item.items || []).join(', ')}</p>
                                {item.description && <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 italic">+ Add items</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {editing && (
            <MenuEditModal
              editing={editing}
              onSave={handleSaveMenu}
              onDelete={handleDelete}
              onClose={() => setEditing(null)}
            />
          )}
        </div>
      )}

      {tab === 'attendance' && (
        <div data-testid="attendance-content">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Label>Date:</Label>
              <Input data-testid="attendance-date" type="date" value={attendanceDate} onChange={e => setAttendanceDate(e.target.value)} className="w-48" />
            </div>
            {summary && (
              <div className="flex gap-3 text-xs">
                {MEALS.map(m => (
                  <div key={m} className="px-3 py-2 bg-gray-50 rounded-lg">
                    <span className="font-medium">{MEAL_ICONS[m]} {m}:</span>
                    <span className="text-green-600 ml-2 font-bold">{summary.by_meal[m]?.attended || 0}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {tenants.length === 0 ? (
            <Card className="p-12 text-center">
              <UtensilsCrossed size={48} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-600">No active tenants in this property</p>
            </Card>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
              <table className="w-full" data-testid="attendance-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Tenant</th>
                    {MEALS.map(m => (
                      <th key={m} className="p-3 text-center text-xs font-semibold text-gray-700 uppercase">{MEAL_ICONS[m]} {m}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tenants.map(t => (
                    <tr key={t.id} className="border-b hover:bg-gray-50" data-testid={`attendance-row-${t.id}`}>
                      <td className="p-3 text-sm font-medium text-gray-900">{t.name}</td>
                      {MEALS.map(m => {
                        const att = attendance[`${t.id}_${m}`];
                        return (
                          <td key={m} className="p-3 text-center">
                            <button
                              data-testid={`attendance-toggle-${t.id}-${m}`}
                              onClick={() => toggleAttendance(t, m)}
                              className="hover:scale-110 transition-transform"
                            >
                              {att ? <CheckCircle className="text-green-600" size={22} /> : <Circle className="text-gray-300" size={22} />}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MenuEditModal = ({ editing, onSave, onDelete, onClose }) => {
  const [items, setItems] = useState((editing.item?.items || []).join(', '));
  const [description, setDescription] = useState(editing.item?.description || '');

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            <span className="capitalize">{editing.day}</span> — {MEAL_ICONS[editing.meal_type]} {editing.meal_type}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Items (comma-separated) *</Label>
            <Input
              data-testid="menu-items-input"
              value={items}
              onChange={e => setItems(e.target.value)}
              placeholder="e.g., Poha, Tea, Fruits"
              autoFocus
            />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Input
              data-testid="menu-desc-input"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="e.g., Light breakfast"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button
              data-testid="menu-save-btn"
              onClick={() => onSave(editing.day, editing.meal_type, items, description)}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              disabled={!items.trim()}
            >
              Save
            </Button>
            {editing.item?.id && (
              <Button data-testid="menu-delete-btn" onClick={() => onDelete(editing.item)} variant="destructive" className="px-3">
                <Trash2 size={16} />
              </Button>
            )}
            <Button data-testid="menu-cancel-btn" onClick={onClose} variant="outline">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FoodMess;
